/**
 * LA ESCALA DE LAS COLUMNAS DE IMPORTE DEL LIBRO — fuente única, ISOMÓRFICA.
 *
 * `bat.transactions` y `bat.imported_transactions` guardan las cantidades de
 * activo en `numeric(30,18)` desde la migración 0357. Antes eran
 * `numeric(20,8)`, y esos 8 decimales NO alcanzaban para representar la unidad
 * nativa de las cadenas que el libro ya importa:
 *
 *     un lamport = 1e-9 SOL   → se redondeaba a la decena de lamports
 *     un wei     = 1e-18 ETH  → se perdía entero
 *
 * Medido contra `api.mainnet-beta.solana.com` (issue #2192), la recompensa de
 * la época 1027 de la cuenta de stake 14TQ21oH2RWg7XFf9nryeHMadwHFPNipYRfCt2FGuaf
 * valía 81.612 lamports en la cadena y 81.610 en el libro. La transferencia de
 * 447.814.119 lamports se guardaba como 447.814.120.
 *
 * ⚠️ POR QUÉ VIVE EN `shared` Y NO EN EL BACKEND (issue #2198). Cuatro
 * validadores del MISMO importe discrepaban —8, 18, 8 y 8— y el que rechazaba
 * con HTTP 400 era el más estrecho: la API negaba un importe de 9 decimales que
 * el importador SÍ escribía en la misma columna. Dos de esos validadores
 * (`utils/validators.ts` aquí, y el formateador CSV) viven en este paquete y no
 * pueden importar del backend, así que mientras la constante fuese
 * backend-only la única forma de escribirlos era con un literal — y un literal
 * es exactamente lo que se desincroniza. La canónica está AQUÍ y el backend la
 * re-exporta desde `@platform/shared-constants/ledger-amount-scale.js`.
 *
 * ⚠️ ESTE NÚMERO NO ES DECORATIVO: aparece en el md5 de deduplicación.
 * PostgreSQL renderiza `numeric::text` con la escala DECLARADA
 * (`1.5::numeric(30,18)::text` → `'1.500000000000000000'`), y el lado JS del
 * mismo hash formatea con `toFixed(LEDGER_AMOUNT_NUMERIC_SCALE)`. Si este valor
 * y la escala real de la columna se separan, los dos lados del hash dejan de
 * coincidir EN SILENCIO y cada resincronización duplica el libro entero. Ver
 * `transaction-deduplication.service.ts`.
 *
 * Cambiar este valor exige, en el MISMO cambio: (1) un `ALTER COLUMN ... TYPE`
 * sobre las dos tablas, y (2) recomputar los `import_hash` ya persistidos
 * —precedente: migraciones 0270, 0280 y 0357—.
 */

// El MISMO `Decimal.clone(...)` que usan los importes en todo el monorepo: los
// guardias del final de este fichero deben leer los números exactamente igual
// que quien escribe en la columna, o volverían a juzgar un valor distinto del
// que se persiste.
import type { Decimal } from 'decimal.js'
import { FinancialAmountDecimal } from '../utils/financial-decimal.js'

export const LEDGER_AMOUNT_NUMERIC_SCALE = 18

/**
 * Precisión total de esas mismas columnas: `numeric(30,18)`.
 *
 * Los DÍGITOS ENTEROS disponibles son `precisión − escala` = 12, EXACTAMENTE
 * los mismos que daba `numeric(20,8)`. El ensanchado añade decimales sin tocar
 * el techo de magnitud, así que el límite de desbordamiento del importador
 * (`MAX_STORABLE_IMPORT_AMOUNT_EXCLUSIVE` = 10^12) no se mueve.
 */
export const LEDGER_AMOUNT_NUMERIC_PRECISION = 30

/**
 * ⚠️⚠️ EL PASO DE LA COLUMNA — el suelo del que deben derivar las guardas de
 * ruido aritmético (issue #2199).
 *
 * Es el importe positivo MÁS PEQUEÑO que `numeric(30,18)` puede representar:
 * `10^-18`. Cualquier valor que la columna guarde es un múltiplo EXACTO de él,
 * así que un importe estrictamente menor no salió de la base — es residuo.
 *
 * POR QUÉ EXISTE ESTA CONSTANTE Y POR QUÉ SE DERIVA. Hasta #2199 las guardas de
 * polvo del libro se escribían `1e-8` a mano. Ese número no era arbitrario:
 * era EXACTAMENTE este paso cuando las columnas eran `numeric(20,8)`. La
 * migración 0357 movió la escala y los literales se quedaron quietos, diez
 * órdenes de magnitud por encima de la precisión real. El caso que lo demuestra
 * vivía en el motor de lotes de coste, que descartaba como «residuo de
 * redondeo» todo lo menor que un satoshi CITANDO esa razón:
 *
 *     «1e-8 is one satoshi — no genuine holding is ever smaller»
 *
 * Un lamport de Solana es `1e-9`, diez veces más pequeño, y en `bat_dev` había
 * 9 filas vivas por debajo de `1e-8` —todas SOL, la menor un lamport exacto— y
 * 10.854 filas con noveno decimal significativo. La frase era cierta al
 * escribirse y dejó de serlo en silencio.
 *
 * ⚠️ NO USES ESTO COMO UMBRAL DE NEGOCIO. Un umbral de negocio dice «por debajo
 * de esto no me importa» y es una decisión de producto que sobrevive a un
 * cambio de escala (p. ej. la tolerancia por defecto al comparar saldos contra
 * el exchange). Esta constante dice «por debajo de esto no es un número, es
 * basura aritmética», y sólo eso.
 */
export const SMALLEST_REPRESENTABLE_LEDGER_AMOUNT = 10 ** -LEDGER_AMOUNT_NUMERIC_SCALE

/**
 * El mismo paso como LITERAL decimal (`'1e-18'`), para quien construye un
 * `Decimal` o compara contra una cadena y no quiere pasar por un `number`.
 */
export const SMALLEST_REPRESENTABLE_LEDGER_AMOUNT_LITERAL = `1e-${LEDGER_AMOUNT_NUMERIC_SCALE}`

/**
 * Dígitos enteros que caben antes de que un INSERT desborde (Postgres 22003).
 * Derivado, no escrito a mano, para que no pueda desincronizarse de los dos
 * valores de arriba.
 */
export const LEDGER_AMOUNT_INTEGER_DIGITS =
  LEDGER_AMOUNT_NUMERIC_PRECISION - LEDGER_AMOUNT_NUMERIC_SCALE

/**
 * El importe entero más grande que cabe en la columna, como literal decimal
 * (`999999999999` con 12 dígitos enteros). Es el techo que anuncian los
 * mensajes de validación, derivado de los dígitos enteros para que el número
 * de la frase y el de la columna no puedan separarse.
 */
export const LEDGER_AMOUNT_MAX_INTEGER_VALUE = 10 ** LEDGER_AMOUNT_INTEGER_DIGITS - 1

/**
 * Cuenta los decimales SIGNIFICATIVOS de un literal decimal ya normalizado a
 * punto (`'0.447814119'` → 9, `'12'` → 0, `'1.500'` → 3).
 *
 * Deliberadamente LÉXICA: cuenta los dígitos escritos, ceros de relleno
 * incluidos, porque de eso es de lo que responde la columna —`numeric(30,18)`
 * acepta `'1.500'` y `'1.500000000000000000'` por igual, y ambos tienen ≤18—.
 * La notación científica no se cuenta aquí: quien la acepta la delega a
 * PostgreSQL, que la redondea a la escala de la columna sin desbordar.
 *
 * Devuelve `null` cuando el valor no es un literal decimal simple, para que el
 * llamante no confunda «no lo sé» con «cero decimales».
 */
export function countDecimalPlacesInPlainLiteral(value: string): number | null {
  const trimmed = value.trim()
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(trimmed)) {
    return null
  }
  const dotIndex = trimmed.indexOf('.')
  return dotIndex === -1 ? 0 : trimmed.length - dotIndex - 1
}

/**
 * ⚠️⚠️ LOS GUARDIAS DE IMPORTE JUZGABAN CON `parseFloat` (issue #2210).
 *
 * `parseFloat` es un parser de PREFIJO y se detiene en la primera coma, así que
 * sobre un importe europeo —el formato del país por defecto del producto— leía
 * un número DISTINTO del que el transformador normaliza y la columna guarda:
 *
 *     parseFloat('0,5')                 === 0    → rechazado como «no positivo»
 *     parseFloat('1.234.567.890.123,5') === 1.234 → pasaba el techo → INSERT 500
 *
 * Las funciones de abajo leen el literal YA NORMALIZADO a punto (el mismo que
 * se persiste) con aritmética decimal EXACTA. Viven aquí, junto a las
 * constantes de las que derivan, porque el criterio y su límite no pueden
 * separarse: ésa fue la causa de #2198 y de #2199.
 */

/** Lo que PostgreSQL acepta en una columna `numeric`: decimal simple o científico. */
const NUMERIC_COLUMN_LITERAL_PATTERN = /^[+-]?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/

/**
 * Lee un literal ya normalizado a punto como Decimal EXACTO, o `null` si no es
 * un literal que la columna pueda aceptar (cadena vacía, basura de cola,
 * `'NaN'`, `'Infinity'`, o un importe europeo todavía sin normalizar).
 */
export function readNormalizedLedgerAmount(normalizedLiteral: string): Decimal | null {
  const trimmed = normalizedLiteral.trim()
  if (!NUMERIC_COLUMN_LITERAL_PATTERN.test(trimmed)) {
    return null
  }
  const amount = new FinancialAmountDecimal(trimmed)
  return amount.isFinite() ? amount : null
}

/** Cantidad de activo: una pata que no mueve nada se omite, nunca se envía como 0. */
export function isNormalizedLedgerAmountStrictlyPositive(normalizedLiteral: string): boolean {
  const amount = readNormalizedLedgerAmount(normalizedLiteral)
  return amount?.greaterThan(0) ?? false
}

/** Valor fiat: el cero SÍ es legítimo (activo sin valor, operación a la par, comisión perdonada). */
export function isNormalizedLedgerAmountZeroOrGreater(normalizedLiteral: string): boolean {
  const amount = readNormalizedLedgerAmount(normalizedLiteral)
  return amount?.greaterThanOrEqualTo(0) ?? false
}

/**
 * El techo de MAGNITUD de la columna, comprobado sobre el valor normalizado.
 *
 * `LEDGER_AMOUNT_MAX_INTEGER_VALUE` deriva de los `LEDGER_AMOUNT_INTEGER_DIGITS`
 * que `numeric(30,18)` reserva para la parte entera, así que nada que pase por
 * aquí puede desbordar el INSERT (22003). Es un pelo MÁS estricto que la
 * columna —`999999999999,5` cabría y aquí no pasa— a propósito: ése es el tope
 * REDONDO que el mensaje de error anuncia al usuario, y prometer uno y aplicar
 * otro es precisamente el desacuerdo que #2198 vino a cerrar.
 *
 * Se compara el VALOR ABSOLUTO porque los negativos los rechaza el guardia de
 * positividad por su propia causa; aquí un `-10^13` sigue sin caber.
 */
export function isWithinLedgerAmountCeiling(normalizedLiteral: string): boolean {
  const amount = readNormalizedLedgerAmount(normalizedLiteral)
  return amount?.abs().lessThanOrEqualTo(LEDGER_AMOUNT_MAX_INTEGER_VALUE) ?? false
}
